/**
 * RBAC-6.1 · One registry for every gateable feature (FR-18, FR-19, FR-20, FR-21a).
 *
 * The registry is KNK-1.55's: `account_modules` (the catalogue) and `account_entitlements` (per
 * account: on, off or trial). Migration 20261102b8 added the five features that used to be
 * `tenant_settings` columns as five more modules. Two layers:
 *   1. A Joules superadmin switches a module per ACCOUNT (Settings → Add-on modules,
 *      `set_account_module`).
 *   2. Inside an account that has it, who may USE it is a permission (or, for Contracts and Solar
 *      Design until RBAC-6.2, the role access list).
 *
 * ⚠️ THIS DECIDES WHAT TO RENDER, AND NOTHING ELSE. The gates are in the database:
 * `account_has_module()`, and `commission_enabled()` for the triggers and workers. A hidden button is
 * not a refusal.
 *
 * CLOSED BY DEFAULT. A failed read, a missing row and an unknown key all read OFF.
 */

/** The five gates that were `tenant_settings` columns before the registry (FR-21a). */
export const GATE_MODULE_KEYS = [
  'contracts',
  'solar_design',
  'commission_costing',
  'site_survey_pm_managed',
  'lead_routing_edge_functions',
] as const;

export type GateModuleKey = (typeof GATE_MODULE_KEYS)[number];

/** One row of `my_account_modules()`: the caller's current account. */
export interface AccountModuleStatus {
  key: string;
  name: string;
  active: boolean;
}

/** The catalogue's own CHECK on `account_modules.key`. */
export const MODULE_KEY_PATTERN = /^[a-z][a-z_]{1,39}$/;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Accepts ANY well-formed key, not a hard-coded list, so a module added to the catalogue reaches the
 * page with no code change (FR-20).
 */
export function parseMyAccountModules(raw: unknown): AccountModuleStatus[] {
  if (!Array.isArray(raw)) return [];
  const out: AccountModuleStatus[] = [];
  for (const r of raw) {
    if (!isRecord(r) || typeof r.key !== 'string' || !MODULE_KEY_PATTERN.test(r.key)) continue;
    out.push({
      key: r.key,
      name: typeof r.name === 'string' && r.name.length > 0 ? r.name : r.key,
      active: r.active === true,
    });
  }
  return out;
}

export function isModuleActive(
  modules: readonly AccountModuleStatus[] | null | undefined,
  key: string,
): boolean {
  if (!Array.isArray(modules)) return false;
  return modules.some((m) => m.key === key && m.active === true);
}

/**
 * FR-19. A feature switched off for an account is invisible to every user in it, holders of the
 * relevant permission and tenant admins included. It is not invisible to a superadmin.
 *
 * ⚠️ Commission is the deliberate exception and does not call this: LDG-1.2 decided a superadmin in an
 * account without it sees what that account's users see (`useCommissionCostingEnabled`), because the
 * database refuses every commission write there and the screens would only offer what cannot work.
 */
export function featureVisibleTo(input: {
  accountEnabled: boolean | null | undefined;
  isSuperadmin: boolean;
}): boolean {
  if (input.isSuperadmin) return true;
  return input.accountEnabled === true;
}
